// client/src/components/Reports.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table } from 'react-bootstrap';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import axios from 'axios';
import * as XLSX from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Reports({ addAlert }) {
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async (startDate = dateRange.start_date, endDate = dateRange.end_date) => {
    try {
      const response = await axios.get(`${API_URL}/reports/generate.php`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      addAlert('Failed to load report data', 'danger');
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateReport = (e) => {
    e.preventDefault();
    fetchReport(dateRange.start_date, dateRange.end_date);
  };

  const exportToExcel = () => {
    if (!reportData) return;
    
    const ws1 = XLSX.utils.json_to_sheet(reportData.usage_report || []);
    const ws2 = XLSX.utils.json_to_sheet(reportData.inventory_summary || []);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, "Usage Report");
    XLSX.utils.book_append_sheet(wb, ws2, "Inventory Summary");
    
    XLSX.writeFile(wb, `Dress_Labels_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getChartData = () => {
    if (!reportData?.inventory_summary) return { labels: [], datasets: [] };

    const labels = reportData.inventory_summary.map(item => item.dress_type);
    const remaining = reportData.inventory_summary.map(item => item.labels_remaining);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Labels Remaining',
          data: remaining,
          backgroundColor: [
            '#8a2be2',
            '#3498db',
            '#2ecc71',
            '#f39c12',
            '#e74c3c'
          ],
          borderColor: [
            '#7a1bd2',
            '#2980b9',
            '#27ae60',
            '#d68910',
            '#c0392b'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const getPieChartData = () => {
    if (!reportData?.usage_report) return { labels: [], datasets: [] };

    // Group by dress type
    const usageByType = {};
    reportData.usage_report.forEach(item => {
      if (!usageByType[item.dress_type]) {
        usageByType[item.dress_type] = 0;
      }
      usageByType[item.dress_type] += parseInt(item.total_labels);
    });

    const labels = Object.keys(usageByType);
    const data = Object.values(usageByType);

    return {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: [
            '#8a2be2',
            '#3498db',
            '#2ecc71',
            '#f39c12',
            '#e74c3c'
          ],
          borderColor: [
            '#7a1bd2',
            '#2980b9',
            '#27ae60',
            '#d68910',
            '#c0392b'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  return (
    <div>
      <h2 className="mb-4">Reports & Analytics</h2>

      {/* Date Range Filter */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleGenerateReport}>
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={dateRange.start_date}
                    onChange={handleDateChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={dateRange.end_date}
                    onChange={handleDateChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button variant="purple" type="submit">
                  <i className="fas fa-chart-bar me-2"></i>
                  Generate Report
                </Button>
              </Col>
              <Col md={3} className="d-flex align-items-end justify-content-end">
                <Button variant="success" onClick={exportToExcel} disabled={!reportData}>
                  <i className="fas fa-file-excel me-2"></i>
                  Export to Excel
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {reportData ? (
        <>
          <Row className="mb-4">
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Inventory by Dress Type</Card.Title>
                  <div className="chart-container">
                    <Bar 
                      data={getChartData()} 
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            display: false
                          }
                        }
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Label Usage Distribution</Card.Title>
                  <div className="chart-container">
                    <Pie 
                      data={getPieChartData()} 
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            position: 'bottom'
                          }
                        }
                      }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Body>
                  <Card.Title>Detailed Usage Report</Card.Title>
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Dress Type</th>
                        <th>Labels Used</th>
                        <th>Rolls Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.usage_report.map((item, index) => (
                        <tr key={index}>
                          <td>{item.date}</td>
                          <td>
                            <span className="badge bg-secondary">{item.dress_type}</span>
                          </td>
                          <td>
                            <strong>{item.total_labels}</strong>
                          </td>
                          <td>{item.rolls_used}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <Card.Body>
                  <Card.Title>Inventory Summary</Card.Title>
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Dress Type</th>
                        <th>Total Rolls</th>
                        <th>Total Labels</th>
                        <th>Labels Used</th>
                        <th>Labels Remaining</th>
                        <th>Low Stock</th>
                        <th>Empty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.inventory_summary.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <strong>{item.dress_type}</strong>
                          </td>
                          <td>{item.total_rolls}</td>
                          <td>{item.total_labels.toLocaleString()}</td>
                          <td>{item.labels_used.toLocaleString()}</td>
                          <td>
                            <span className={item.labels_remaining <= 1000 ? 'text-warning fw-bold' : ''}>
                              {item.labels_remaining.toLocaleString()}
                            </span>
                          </td>
                          <td>
                            {item.low_stock > 0 ? (
                              <span className="badge bg-warning">{item.low_stock}</span>
                            ) : (
                              <span className="text-muted">0</span>
                            )}
                          </td>
                          <td>
                            {item.empty_stock > 0 ? (
                              <span className="badge bg-danger">{item.empty_stock}</span>
                            ) : (
                              <span className="text-muted">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        <div className="text-center py-5">
          <i className="fas fa-chart-pie fa-3x text-muted mb-3"></i>
          <p>Select a date range and generate a report</p>
        </div>
      )}
    </div>
  );
}

export default Reports;